/**
 * Versioned Terms-of-Use / Content-Policy acceptance (docs/077-IN-APP-TERMS-
 * ACKNOWLEDGEMENT.md v0). A user's acceptance is recorded as a plain version number
 * (`Settings.acceptedTermsVersion`) rather than a boolean, so a *future* release that
 * materially changes the terms (a new capability, a new data use, …) can bump
 * the relevant entry in {@link SURFACE_TERMS_VERSIONS} and every user — new
 * install or long-time user alike —
 * is re-prompted exactly once, the next time they open the app, rather than only on
 * first install.
 *
 * Pure and framework-free so the gate's core decision is unit-tested without RN/expo-
 * router; the blocking UI itself lives in `src/ui/TermsGate.tsx` + `app/terms-accept.tsx`.
 */

/**
 * App-surface Terms versions advance independently (docs/376 v0). A Roku-only
 * change must not re-gate mobile, and vice versa.
 */
export const SURFACE_TERMS_VERSIONS = {
  mobile: 2,
  roku: 1,
} as const;

/**
 * The mobile terms/content-policy version. Kept as the existing public constant
 * so the mobile gate and persisted Settings shape remain byte-for-byte compatible.
 */
export const CURRENT_TERMS_VERSION = SURFACE_TERMS_VERSIONS.mobile;

/**
 * Whether the gate should block until the user (re-)accepts.
 *
 * - `acceptedVersion` is `null`/`undefined` (never accepted — covers BOTH a brand-new
 *   install, whose settings blob has never carried the field, AND an existing user
 *   updating from a build that predates this feature, whose persisted settings simply
 *   lack the key) → `true`.
 * - `acceptedVersion` is below `currentVersion` (the terms changed since they last
 *   accepted) → `true`.
 * - `acceptedVersion` is at or above `currentVersion` (already accepted the current —
 *   or a newer, e.g. a downgrade scenario — terms) → `false`.
 */
export function needsTermsAcceptance(
  acceptedVersion: number | null | undefined,
  currentVersion: number = CURRENT_TERMS_VERSION,
): boolean {
  if (acceptedVersion == null) return true;
  return acceptedVersion < currentVersion;
}
